import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import authSlice from "../../store/slices/auth.slice";
import { StoreKeeper } from "../../store/store";
import i18n from "../localization/i18n";
import { AUTH_URL } from "./endpoints";

class TokenManager {
  private static refreshPromise: Promise<string | null> | null = null;

  static getAccessToken(): string | null {
    return localStorage.getItem("accessToken");
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken");
  }

  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("token", accessToken); // Backward compatibility
  }

  static clearTokens(): void {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("token");
  }

  static async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    this.refreshPromise = this.performTokenRefresh(refreshToken);

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private static async performTokenRefresh(
    refreshToken: string,
  ): Promise<string | null> {
    try {
      const response = await axios.post(
        `${AUTH_URL}/refresh`,
        {
          refreshToken,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "device-type": "web",
          },
        },
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Update tokens (newRefreshToken might be the same as old one)
      this.setTokens(accessToken, newRefreshToken || refreshToken);

      return accessToken;
    } catch (error: any) {
      console.error("Token refresh failed:", error);

      // Check if it's a refresh token invalidation error
      if (error?.response?.status === 401) {
        // Show user-friendly message about multi-device login
        this.handleRefreshTokenExpired();
      }

      this.clearTokens();
      return null;
    }
  }

  private static handleRefreshTokenExpired(): void {
    // You can show a modal or notification to the user
    const event = new CustomEvent("refreshTokenExpired", {
      detail: {
        message:
          "Your session has expired due to login from another device. Please log in again.",
        showModal: true,
      },
    });
    window.dispatchEvent(event);
  }

  static isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }
}

const defaultOptions = {
  headers: {
    "Content-Type": "application/hal+json",
    // accept: "text/html",
    // "device-type": "web",
  },
};

export const instance = axios.create(defaultOptions);

// Request Interceptor
instance.interceptors.request.use(
  async (config) => {
    let token =
      TokenManager.getAccessToken() || localStorage.getItem("token") || "";
    const appVersion = localStorage.getItem("appVersion");

    // Check if token is expired and try to refresh
    if (token && TokenManager.isTokenExpired(token)) {
      const newToken = await TokenManager.refreshAccessToken();
      token = newToken || "";
    }

    config.headers!.Authorization = token ? `Bearer ${token}` : "";
    config.headers!["app-version"] = appVersion ?? "";
    config.headers!.Lang = i18n.language ?? "gb";
    return config;
  },
  (error) => {
    console.error("Request setup error:", error);
    return Promise.reject(error);
  },
);

// Response Interceptor for catching HTTP errors and handling token refresh
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { status, data } = error.response ?? {};
    const originalRequest = error.config;

    // Handle token expiration
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newToken = await TokenManager.refreshAccessToken();
      if (newToken) {
        // Update the authorization header and retry the request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } else {
        TokenManager.clearTokens();
        StoreKeeper.store.dispatch(authSlice.actions.setLogoutState());
      }
    }

    return Promise.reject(error);
  },
);

const responseBody = (response: AxiosResponse) => response.data;

export const requests = {
  get: (url: string, params?: any) =>
    instance
      .get(url, { params })
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  post: (url: string, body: {}, customHeaders?: any) =>
    instance
      .post(url, body, customHeaders)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  put: (
    url: string,
    body: {},
    customHeaders?: AxiosRequestConfig<{}> | undefined,
  ) =>
    instance
      .put(url, body, customHeaders)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  patch: (url: string, body: {}, customHeaders?: any) =>
    instance
      .patch(url, body, customHeaders)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
  delete: (url: string) =>
    instance
      .delete(url)
      .then(responseBody)
      .catch((e) => {
        throw e;
      }),
};

export const downloadFile = (
  url: string,
  format?: string,
  fallbackFileName?: string,
) => {
  const token =
    TokenManager.getAccessToken() || localStorage.getItem("token") || "";
  const appVersion = localStorage.getItem("appVersion");

  return axios
    .request({
      url,
      method: "GET",
      responseType: "blob",
      headers: {
        "app-version": appVersion ?? "",
        Authorization: token ? `Bearer ${token}` : "",
      },
    })
    .then((response) => {
      const contentDisposition = response.headers["content-disposition"];
      let fileName = fallbackFileName ?? "file";

      if (contentDisposition) {
        const extractedName =
          getFileNameFromContentDisposition(contentDisposition);
        if (extractedName) {
          fileName = extractedName;
        }
      }

      if (
        format &&
        !fileName.toLowerCase().endsWith(`.${format.toLowerCase()}`)
      ) {
        fileName = `${fileName}.${format}`;
      }

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
};

export const formDataHeaders = {
  headers: {
    "Content-Type": "multipart/form-data",
  },
};

function getFileNameFromContentDisposition(header: string): string | null {
  const fileNameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(header);
  if (fileNameMatch != null && fileNameMatch[1]) {
    return fileNameMatch[1].replace(/['"]/g, "");
  }
  return null;
}

export { TokenManager };
